import type { WireInsight, Signal } from "@/types/api";

/**
 * Normalize a raw WS/REST insight into the UI `Signal` shape.
 * Pure + deterministic — covered by normalize.test.ts.
 */
export function normalizeSignal(s: WireInsight): Signal {
  const a0 = (s.affectedAssets ?? [])[0];
  const lat = Number(s.lat);
  const lon = Number(s.lon);
  return {
    id: s.id,
    title: s.title || s.summary || s.ruleName || "Signal",
    severity: s.severity ?? "low",
    // "Where is this": for site-anchored signals (fire/outage) the first asset IS the place;
    // for a quake the place is the EPICENTER — naming the first shaken asset made a quake's
    // location read as "gas pipeline P5805". Precise coords render right next to this in the
    // detail header.
    region: /quake|earthquake|seismic/i.test(s.ruleName ?? "")
      ? "epicenter"
      : a0?.name ??
        a0?.country ??
        `${lat.toFixed(1)}, ${lon.toFixed(1)}`,
    tickers: s.tickers ?? [],
    rule: s.ruleName ?? "",
    summary: s.summary ?? "",
    recommendedAction: s.recommendedAction ?? "",
    lat,
    lon,
    // SERVER truth only — never stamp the client clock. The old Date.now() fallback made the
    // same signal read "5s ago" on a fresh login and "5m ago" on an older session. A 0 renders
    // as "—" downstream instead of a fabricated age.
    createdAt: s.createdAt ?? s.updatedAt ?? 0,
    updatedAt: s.updatedAt ?? s.createdAt ?? 0,
    assets: (s.affectedAssets ?? []).map((a) => ({
      name: a.name,
      kind: a.type,
      distKm: a.distanceKm,
      ticker: a.ticker,
      country: a.country,
      // site coordinates ride the wire (RefAsset) — the map highlights them for the selection
      lat: typeof a.lat === "number" ? a.lat : undefined,
      lon: typeof a.lon === "number" ? a.lon : undefined,
    })),
    triggers: [
      ...(s.triggerEventIds ?? []),
      ...(s.triggerObjectIds ?? []),
    ],
    confidence: s.confidence ?? null,
    confidenceRate: s.confidenceRate ?? null,
    estimatedEffect: s.estimatedEffect ?? null,
    status: s.status ?? 'live',
    resolvedAt: s.resolvedAt ?? null,
    statusReliable: s.statusReliable ?? false,
    parentId: s.parentId ?? null,
    rootId: s.rootId ?? null,
    isRoot: s.isRoot ?? true,
    engineIngressAt: s.engineIngressAt ?? null,
    enginePublishedAt: s.enginePublishedAt ?? null,
    raw: s,
  };
}

/** Server-clock offset (server − client), learned by ws-client from snapshot/hb frames. Every
 *  "x ago" below renders against the CORRECTED clock, so a device with a skewed local clock
 *  shows the same age as every other device — event times are server truth, full stop. */
let clockOffsetMs = 0;
export function setClockOffset(ms: number): void {
  clockOffsetMs = ms;
}
export function serverNow(): number {
  return Date.now() + clockOffsetMs;
}

/** Human "x ago" formatting used across the feed + detail. 0/missing timestamp → "—". */
export function timeAgo(ts: number, now = serverNow()): string {
  if (!ts) return "—";
  const s = Math.max(0, (now - ts) / 1000);
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

/** Precise UTC wall clock — "12:34:56 UTC". Pairs with timeAgo so every event shows BOTH the
 *  relative age and the exact moment it happened. Empty for a missing timestamp. */
export function utcClock(ts: number): string {
  if (!ts) return "";
  const d = new Date(ts);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())} UTC`;
}

/** Situation age: primary = when it STARTED (createdAt) as "age · HH:MM:SS UTC"; appends
 *  "· upd Xm" when the last revision is meaningfully newer. Situations (outages, clusters)
 *  live for hours and revise on every new event — showing only updatedAt made a 10h-old
 *  situation read as "7m ago". */
export function signalAge(createdAt: number, updatedAt: number, now = serverNow()): string {
  if (!createdAt) return timeAgo(updatedAt, now);
  const age = `${timeAgo(createdAt, now)} · ${utcClock(createdAt)}`;
  if (updatedAt - createdAt < 60_000) return age;
  return `${age} · upd ${timeAgo(updatedAt, now).replace(" ago", "")}`;
}

/** Collapse a rule name / trigger id into a short tag. */
export function ruleTag(rule: string): string {
  return rule.replace(/[-_]/g, " ").toUpperCase().slice(0, 22);
}

/** Event ids are namespaced by their adapter ("firms:…", "entsoe:…"). When the full event
 *  object isn't at hand (standalone page, expired from the live ring), the SOURCE is the
 *  meaningful part — render "FIRMS satellite (a1b2c3d4)" instead of a bare hex tail. */
const EVENT_SOURCE_LABELS: Array<[RegExp, string]> = [
  [/^firms/i, "FIRMS satellite"],
  [/^entsoe/i, "ENTSO-E filing"],
  [/^elexon/i, "Elexon REMIT"],
  [/^(nordpool|nord-pool|umm)/i, "Nord Pool UMM"],
  [/^usgs/i, "USGS seismic"],
  [/^emsc/i, "EMSC seismic"],
  [/^(eq|quake|seis|gq)/i, "Seismic detector"],
  [/^(gdelt|news)/i, "News wire"],
  [/^eonet/i, "NASA EONET"],
  [/^(adsb|aircraft)/i, "ADS-B aircraft"],
  [/^(ais|vessel)/i, "AIS vessel"],
  [/^(noaa|nws|weather)/i, "Weather service"],
];

export function sourceLabelForEventId(id: string): string {
  const hit = EVENT_SOURCE_LABELS.find(([re]) => re.test(id));
  const short = shortTrigger(id).slice(0, 8);
  return hit ? `${hit[1]} (${short})` : shortTrigger(id);
}

/** Shorten a trigger id or URL to a readable token. */
export function shortTrigger(t: string): string {
  const m = String(t);
  const u = m.indexOf("http");
  if (u >= 0) {
    try {
      return new URL(m.slice(u)).hostname.replace(/^www\./, "");
    } catch {
      /* fall through */
    }
  }
  const seg = m.split(":");
  return seg[seg.length - 1].slice(0, 16) || m.slice(0, 16);
}
